import { gsap } from 'gsap'

/**
 * ExpandableElement
 *
 * Animated expand/collapse for elements with height restrictions.
 *
 * Two measuring modes:
 *
 * 1. 'class' mode - For CSS restrictions like line-clamp
 *    - Temporarily adds measuring class to disable restrictions
 *    - Measures content, removes class, then animates
 *    - CSS: .is-measuring { -webkit-line-clamp: unset; }
 *
 * 2. 'content' mode - For max-height: 0 patterns (accordions)
 *    - Wrapper has max-height: 0, content inside has natural height
 *    - Measures content directly, animates wrapper height
 *    - On collapse: resets max-height: 0
 *
 * The expanded class is added when expand() starts. By default it is removed
 * when the collapse animation finishes; expandedClassTiming: 'immediate'
 * removes it when collapse() starts instead, so CSS hanging off it (colours,
 * icon rotation) reacts to the toggle rather than to the height animation.
 *
 * HTML Structure:
 *   <div class="js-expandable">                    <!-- element -->
 *     <div class="expandable-element__wrapper">    <!-- animated -->
 *       <div class="expandable-element__content">  <!-- measured -->
 *         ...content...
 *       </div>
 *     </div>
 *     <button class="js-toggle">Toggle</button>    <!-- optional -->
 *   </div>
 *
 * @example
 * new ExpandableElement({
 *   element: document.querySelector('.js-expandable'),
 *   toggleElement: document.querySelector('.js-toggle'),
 *   measureMode: 'content', // or 'class'
 * })
 */

// -------------------------------------------------------------------------- //
// Configuration
// -------------------------------------------------------------------------- //
const DEFAULT_OPTIONS = {
	// DOM Elements
	element: null,
	toggleElement: null,

	// Selectors (for querying within element)
	wrapperSelector: '.expandable-element__wrapper',
	contentSelector: '.expandable-element__content',

	// Measuring approach
	// 'class' = add measuring class to temporarily disable restrictions (e.g., line-clamp)
	// 'content' = measure content element directly (for max-height: 0 pattern)
	measureMode: 'content',
	measuringClass: 'is-measuring',

	// State classes
	expandableClass: 'is-expandable',
	expandedClass: 'is-expanded',

	// When expandedClass is removed on collapse:
	// 'settled'   = after the collapse animation finishes
	// 'immediate' = the moment collapse starts, so CSS keyed off it
	//               animates in step with the toggle
	expandedClassTiming: 'settled',

	// Initial State
	initialState: 'collapsed', // 'expanded' or 'collapsed'

	// Animation
	animationDuration: 0.5,
	ease: 'power4.out',
	useSpeedBasedDuration: false,
	animationSpeed: 600, // pixels per second (when useSpeedBasedDuration is true)
	minDuration: 0,
	maxDuration: 2,

	// Behavior
	hideToggleOnExpand: false,
	checkExpandability: true, // Whether to check if content exceeds collapsed height

	// Callbacks
	cbOnChange: null, // (state, instance) => {} - called when animation starts
	cbOnAfterChange: null, // (state, instance) => {} - called when animation completes
}

// -------------------------------------------------------------------------- //
// Helper: Calculate duration based on distance
// -------------------------------------------------------------------------- //
function calcDurationFromDistance(fromHeight, toHeight, options) {
	const { animationSpeed, minDuration, maxDuration, ease } = options
	const distance = Math.abs(toHeight - fromHeight)
	let duration = distance / animationSpeed

	// Clamp duration
	duration = Math.max(minDuration, Math.min(maxDuration, duration))

	return { duration, ease }
}

// -------------------------------------------------------------------------- //
// ExpandableElement Class
// -------------------------------------------------------------------------- //
class ExpandableElement {
	constructor(options) {
		this.options = { ...DEFAULT_OPTIONS, ...options }

		// Cache DOM elements
		this.elContainer = this.options.element
		this.elToggle = this.options.toggleElement
		this.elWrapper = this.elContainer.querySelector(this.options.wrapperSelector)
		this.elContent = this.elContainer.querySelector(this.options.contentSelector)

		// Validate required elements
		if (!this.elContainer || !this.elWrapper || !this.elContent) {
			console.warn('ExpandableElement: Missing required DOM elements.', {
				container: this.elContainer,
				wrapper: this.elWrapper,
				content: this.elContent,
			})
			return
		}

		// State
		this.isExpanded = false
		this.isExpandable = true
		this.isAnimating = false
		this.collapsedHeight = 0

		// Bind methods
		this.bndToggle = this.toggle.bind(this)

		// Initialize
		this.init()
	}

	// --------------------------------------------------------------------------
	// Initialization
	// --------------------------------------------------------------------------
	init() {
		// Add toggle listener
		if (this.elToggle) {
			this.elToggle.addEventListener('click', this.bndToggle)
		}

		// Check expandability and prepare
		this.prepare()

		// Set initial state (without animation)
		if (this.options.initialState === 'expanded') {
			this.setExpanded()
		}
	}

	// --------------------------------------------------------------------------
	// Prepare / Check Expandability
	// --------------------------------------------------------------------------
	prepare() {
		// Store collapsed height
		this.collapsedHeight = this.elWrapper.offsetHeight

		if (this.options.checkExpandability) {
			const expandedHeight = this.measureExpandedHeight()
			this.isExpandable = expandedHeight > this.collapsedHeight

			if (this.isExpandable) {
				this.elContainer.classList.add(this.options.expandableClass)
			} else {
				this.elContainer.classList.remove(this.options.expandableClass)
			}
		} else {
			// Always expandable
			this.isExpandable = true
			this.elContainer.classList.add(this.options.expandableClass)
		}
	}

	// --------------------------------------------------------------------------
	// Set Expanded (no animation)
	// --------------------------------------------------------------------------
	setExpanded() {
		if (!this.isExpandable) return

		this.isExpanded = true
		this.elContainer.classList.add(this.options.expandedClass)

		if (this.options.measureMode === 'content') {
			gsap.set(this.elWrapper, { maxHeight: 'none' })
		}

		if (this.elToggle) {
			this.elToggle.classList.add('is-toggled')
			this.elToggle.setAttribute('aria-expanded', 'true')
		}

		if (this.options.hideToggleOnExpand && this.elToggle) {
			this.elToggle.style.display = 'none'
		}
	}

	// --------------------------------------------------------------------------
	// Set Collapsed (no animation)
	// --------------------------------------------------------------------------
	setCollapsed() {
		this.isExpanded = false
		this.elContainer.classList.remove(this.options.expandedClass)

		if (this.options.measureMode === 'content') {
			gsap.set(this.elWrapper, { maxHeight: 0 })
		}

		if (this.elToggle) {
			this.elToggle.classList.remove('is-toggled')
			this.elToggle.setAttribute('aria-expanded', 'false')
		}
	}

	// --------------------------------------------------------------------------
	// Measure Expanded Height
	// --------------------------------------------------------------------------
	measureExpandedHeight() {
		let expandedHeight

		if (this.options.measureMode === 'class') {
			// Temporarily add measuring class to disable restrictions
			this.elContainer.classList.add(this.options.measuringClass)
			expandedHeight = this.elContent.offsetHeight
			this.elContainer.classList.remove(this.options.measuringClass)
		} else {
			// Measure content directly (for max-height: 0 pattern)
			expandedHeight = this.elContent.offsetHeight
		}

		return expandedHeight
	}

	// --------------------------------------------------------------------------
	// Get Animation Duration
	// --------------------------------------------------------------------------
	getAnimationParams(fromHeight, toHeight) {
		if (this.options.useSpeedBasedDuration) {
			return calcDurationFromDistance(fromHeight, toHeight, this.options)
		}

		return {
			duration: this.options.animationDuration,
			ease: this.options.ease,
		}
	}

	// --------------------------------------------------------------------------
	// Expand
	// --------------------------------------------------------------------------
	expand() {
		if (!this.isExpandable || this.isExpanded || this.isAnimating) return

		this.isAnimating = true
		this.isExpanded = true

		// Store current collapsed height (may have changed since init)
		this.collapsedHeight = this.elWrapper.offsetHeight

		// Measure expanded height
		const expandedHeight = this.measureExpandedHeight()
		// Get animation parameters
		const { duration, ease } = this.getAnimationParams(this.collapsedHeight, expandedHeight)

		// Set current height to enable animation

		if (this.options.measureMode === 'content') {
			gsap.set(this.elWrapper, {
				height: this.collapsedHeight,
				maxHeight: 'none',
			})
		} else {
			gsap.set(this.elWrapper, { height: this.collapsedHeight })
		}

		// Add expanded class
		this.elContainer.classList.add(this.options.expandedClass)

		// Update toggle state
		if (this.elToggle) {
			this.elToggle.classList.add('is-toggled')
			this.elToggle.setAttribute('aria-expanded', 'true')
		}

		// Trigger onChange callback
		this.triggerCallback('cbOnChange', 'expanded')

		// Animate
		gsap.to(this.elWrapper, {
			height: expandedHeight,
			duration,
			ease,
			onComplete: () => {
				// Clear inline height to allow natural flow
				gsap.set(this.elWrapper, { clearProps: 'height' })

				this.isAnimating = false

				// Hide toggle if option is set
				if (this.options.hideToggleOnExpand && this.elToggle) {
					this.elToggle.style.display = 'none'
				}

				// Trigger onAfterChange callback
				this.triggerCallback('cbOnAfterChange', 'expanded')
			},
		})
	}

	// --------------------------------------------------------------------------
	// Collapse
	// --------------------------------------------------------------------------
	collapse() {
		if (!this.isExpanded || this.isAnimating) return

		this.isAnimating = true
		this.isExpanded = false

		if (this.options.expandedClassTiming === 'immediate') {
			this.elContainer.classList.remove(this.options.expandedClass)
		}

		// Get current height
		const currentHeight = this.elWrapper.offsetHeight

		// Get animation parameters
		const { duration, ease } = this.getAnimationParams(currentHeight, this.collapsedHeight)

		// Set current height to enable animation
		gsap.set(this.elWrapper, { height: currentHeight })

		// Update toggle state
		if (this.elToggle) {
			this.elToggle.classList.remove('is-toggled')
			this.elToggle.setAttribute('aria-expanded', 'false')
		}

		// Trigger onChange callback
		this.triggerCallback('cbOnChange', 'collapsed')

		// Animate
		gsap.to(this.elWrapper, {
			height: this.collapsedHeight,
			duration,
			ease,
			onComplete: () => {
				// Remove expanded class
				if (this.options.expandedClassTiming !== 'immediate') {
					this.elContainer.classList.remove(this.options.expandedClass)
				}

				if (this.options.measureMode === 'content') {
					gsap.set(this.elWrapper, {
						clearProps: 'height',
						maxHeight: 0,
					})
				} else {
					gsap.set(this.elWrapper, { clearProps: 'height' })
				}

				// Clear inline height
				// gsap.set(this.elWrapper, { clearProps: 'height' })

				this.isAnimating = false

				// Trigger onAfterChange callback
				this.triggerCallback('cbOnAfterChange', 'collapsed')
			},
		})
	}

	// --------------------------------------------------------------------------
	// Toggle
	// --------------------------------------------------------------------------
	toggle() {
		if (this.isExpanded) {
			this.collapse()
		} else {
			this.expand()
		}
	}

	// --------------------------------------------------------------------------
	// Refresh (recalculate collapsed height)
	// --------------------------------------------------------------------------
	refresh() {
		if (this.isAnimating) return

		if (!this.isExpanded) {
			gsap.set(this.elWrapper, { clearProps: 'height' })
			this.collapsedHeight = this.elWrapper.offsetHeight
		}

		this.prepare()
	}

	// --------------------------------------------------------------------------
	// Callbacks
	// --------------------------------------------------------------------------
	triggerCallback(callbackName, state) {
		const callback = this.options[callbackName]
		if (typeof callback === 'function') {
			callback(state, this)
		}
	}

	// --------------------------------------------------------------------------
	// Destroy
	// --------------------------------------------------------------------------
	destroy() {
		// Remove event listeners
		if (this.elToggle) {
			this.elToggle.removeEventListener('click', this.bndToggle)
		}

		// Remove classes
		this.elContainer.classList.remove(this.options.expandableClass)
		this.elContainer.classList.remove(this.options.expandedClass)

		// Clear inline styles
		gsap.set(this.elWrapper, { clearProps: 'height' })

		// Nullify references
		this.elContainer = null
		this.elToggle = null
		this.elWrapper = null
		this.elContent = null
	}
}

export { ExpandableElement }

// import { gsap } from 'gsap'

// /**
//  * ExpandableElement class
//  * @class
//  * @classdesc Class for creating expandable elements that animate height on toggle
//  */
// class ExpandableElement {
// 	constructor(options) {
// 		// Default options
// 		const defaultOptions = {
// 			element: null, // The main container element
// 			toggleElement: null, // The button to toggle expand/collapse
// 			expandedClass: 'is-expanded', // Class added when expanded
// 			wrapperClass: 'expandable-element__inner', // Class for the inner wrapper
// 			contentClass: 'expandable-element__content', // Class for the content area
// 			animationDuration: 0.5, // Duration of the animation
// 			ease: 'power4.out', // Easing function for the animation
// 			hideToggleOnExpand: false, // Whether to hide the toggle button after expanding
// 			measuringClass: 'is-measuring', // Class added temporarily for measuring expanded height
// 		}

// 		// Merge user-provided options with defaults
// 		this.options = { ...defaultOptions, ...options }

// 		// Assign DOM elements
// 		this.elContainer = this.options.element
// 		this.elToggle = this.options.toggleElement
// 		this.elWrapper = this.elContainer.querySelector(
// 			`.${this.options.wrapperClass}`
// 		)
// 		this.elContent = this.elContainer.querySelector(
// 			`.${this.options.contentClass}`
// 		)

// 		// Ensure required elements exist
// 		if (!this.elContainer || !this.elWrapper || !this.elContent) {
// 			console.warn('ExpandableElement: Missing required DOM elements.')
// 			return
// 		}

// 		// State
// 		this.isExpanded = false
// 		this.initialHeight = 0 // To store the collapsed height

// 		// Bind the toggle method
// 		this.bndToggle = this.toggle.bind(this)

// 		// Add event listener to the toggle button
// 		if (this.elToggle) {
// 			this.elToggle.addEventListener('click', this.bndToggle)
// 		}

// 		// Set initial height
// 		this.prepare()
// 	}

// 	/**
// 	 * Prepare the element by setting its initial collapsed height.
// 	 */
// 	prepare() {
// 		// Set the initial height
// 		// gsap.set(this.elWrapper, { height: this.initialHeight })
// 	}

// 	/**
// 	 * Measure the expanded height of the content.
// 	 * @returns {number} - The expanded height in pixels.
// 	 */
// 	measureExpandedHeight() {
// 		// Temporarily add the measuring class to disable styles like line-clamp
// 		this.elContainer.classList.add(this.options.measuringClass)

// 		// Measure the full height
// 		const expandedHeight = this.elContent.offsetHeight

// 		// Remove the measuring class
// 		this.elContainer.classList.remove(this.options.measuringClass)

// 		return expandedHeight
// 	}

// 	/**
// 	 * Expand the element.
// 	 */
// 	expand() {
// 		// Record the initial (collapsed) height
// 		this.initialHeight = this.elWrapper.offsetHeight

// 		this.isExpanded = true

// 		// Measure the expanded height
// 		const expandedHeight = this.measureExpandedHeight()

// 		gsap.set(this.elWrapper, { height: this.elWrapper.offsetHeight }) // Set current height before animating

// 		this.elContainer.classList.add(this.options.expandedClass)

// 		// // Animate to the expanded height
// 		gsap.to(this.elWrapper, {
// 			height: expandedHeight,
// 			duration: this.options.animationDuration,
// 			ease: this.options.ease,
// 			onComplete: () => {
// 				gsap.set(this.elWrapper, { clearProps: 'height' }) // Clear inline height

// 				// Trigger the onChange callback
// 				this.triggerOnChange('expanded')
// 			},
// 		})

// 		// // Optionally hide the toggle button
// 		if (this.options.hideToggleOnExpand && this.elToggle) {
// 			this.elToggle.style.display = 'none'
// 		}
// 	}

// 	/**
// 	 * Collapse the element.
// 	 */
// 	collapse() {
// 		this.isExpanded = false

// 		// Animate back to the initial (collapsed) height
// 		gsap.to(this.elWrapper, {
// 			height: this.initialHeight,
// 			duration: this.options.animationDuration,
// 			ease: this.options.ease,
// 			onComplete: () => {
// 				this.elContainer.classList.remove(this.options.expandedClass)
// 				gsap.set(this.elWrapper, { clearProps: 'height' }) // Clear inline height
// 				// Trigger the onChange callback
// 				this.triggerOnChange('collapsed')
// 			},
// 		})
// 	}

// 	/**
// 	 * Trigger the onChange callback.
// 	 * @param {string} state - The new state ('expanded' or 'collapsed').
// 	 */
// 	triggerOnChange(state) {
// 		if (typeof this.options.cbOnChange === 'function') {
// 			this.options.cbOnChange(state)
// 		}
// 	}

// 	/**
// 	 * Toggle between expand and collapse.
// 	 */
// 	toggle() {
// 		if (this.isExpanded) {
// 			this.collapse()
// 		} else {
// 			this.expand()
// 		}
// 	}

// 	/**
// 	 * Destroy the instance and clean up.
// 	 */
// 	destroy() {
// 		if (this.elToggle) {
// 			this.elToggle.removeEventListener('click', this.bndToggle)
// 		}
// 		this.elContainer = null
// 		this.elToggle = null
// 		this.elWrapper = null
// 		this.elContent = null
// 	}
// }
// export { ExpandableElement }
